import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, X, ZoomIn, ImageIcon } from "lucide-react";
import ChapterHeader from "./ChapterHeader";
import Lightbox from "./Lightbox";
import { CASE_STUDIES } from "../data/caseStudies";

const DIAGRAM_BASE = "/diagrams/";

/* Animated flow strip — the card's visual signature (kept from the previous design). */
function FlowStrip({ code, nodes }) {
  return (
    <div className="relative aspect-[16/9] overflow-hidden border-b border-border bg-[#0A0E17]" aria-hidden="true">
      <div
        className="absolute inset-0 opacity-40"
        style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.035) 1px, transparent 1px)", backgroundSize: "24px 24px" }}
      />
      <div className="absolute inset-0 flex items-center justify-center px-6">
        <div className="flex items-center w-full max-w-[420px]">
          {nodes.map((n, i) => (
            <div key={n} className="flex items-center flex-1 min-w-0">
              <div
                className={`flex-1 min-w-0 border px-2 py-2 text-center font-mono text-[9px] md:text-[10px] uppercase tracking-[0.1em] leading-tight ${
                  i === 1 ? "border-accent text-accent bg-accent/10" : "border-[#3A4356] text-foreground/80 bg-[#111827]"
                }`}
              >
                {n}
              </div>
              {i < nodes.length - 1 && (
                <div className="relative w-8 md:w-10 shrink-0 h-px bg-accent/70">
                  <span className="flow-dot absolute -top-[2.5px] left-0 w-1.5 h-1.5 bg-accent" style={{ animationDelay: `${i * 0.6}s` }} />
                  <span className="absolute -right-[1px] -top-[3px] border-y-[3.5px] border-y-transparent border-l-[6px] border-l-accent" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-background/70 via-transparent to-transparent pointer-events-none" />
      <div className="absolute top-4 left-4 font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-accent bg-background/70 backdrop-blur px-2.5 py-1 border border-accent/50">
        {code}
      </div>
    </div>
  );
}

function ArchitectureImage({ diagram, onZoom }) {
  const [missing, setMissing] = useState(false);
  const src = `${DIAGRAM_BASE}${diagram.file}`;
  if (missing) {
    return (
      <div
        data-testid="architecture-placeholder"
        className="relative aspect-[16/9] border border-dashed border-accent/50 bg-[#0A0E17] grid place-items-center text-center p-6"
      >
        <div>
          <ImageIcon className="mx-auto text-accent/70" size={22} />
          <div className="mt-3 font-heading font-black uppercase tracking-tight text-base md:text-lg">{diagram.title}</div>
          <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Diagram pending · {diagram.file}
          </div>
        </div>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onZoom({ image: src, title: diagram.title })}
      data-testid="architecture-image-button"
      className="group relative block w-full border border-border bg-[#0A0E17] overflow-hidden text-left"
      aria-label={`Enlarge: ${diagram.title}`}
    >
      <img src={src} alt={diagram.title} loading="lazy" decoding="async" onError={() => setMissing(true)} className="w-full h-auto block" />
      <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] bg-background/80 backdrop-blur border border-border px-2.5 py-1.5 text-foreground/80 group-hover:text-accent group-hover:border-accent transition-colors">
        <ZoomIn size={12} /> Enlarge
      </span>
    </button>
  );
}

function Card({ p, index, open, onToggle }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ duration: 0.8, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className={`group relative flex flex-col bg-surface border overflow-hidden transition-colors ${open ? "border-accent" : "border-border hover:border-accent"}`}
      data-testid={`project-card-${p.code}`}
    >
      <FlowStrip code={p.code} nodes={p.flow} />
      <div className="p-6 md:p-8 flex flex-col flex-1">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground truncate">{p.scope}</div>
          {p.featured && <span className="shrink-0 font-mono text-[9px] uppercase tracking-[0.25em] text-accent border border-accent/60 px-2 py-0.5">Featured</span>}
        </div>
        <h3 className="font-heading text-2xl md:text-3xl font-black tracking-tight leading-[1.05] uppercase group-hover:text-accent transition-colors">{p.title}</h3>
        <p className="mt-4 text-sm md:text-[15px] leading-relaxed text-foreground/80">{p.body}</p>
        <p className="mt-4 font-mono text-[11px] leading-relaxed tracking-[0.08em] text-accent" data-testid={`project-result-${p.code}`}>
          {p.result}
        </p>
        <div className="mt-auto pt-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {p.tags.map((t) => (
              <span key={t} className="font-mono text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 border border-border text-foreground/70">
                {t}
              </span>
            ))}
          </div>
          {p.featured && (
            <button
              type="button"
              onClick={() => onToggle(open ? null : p.code)}
              aria-expanded={open}
              aria-controls="case-study-panel"
              data-testid={`project-case-study-${p.code}`}
              className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-accent hover:text-foreground transition-colors whitespace-nowrap"
            >
              {open ? "Close" : "Read the case study"} <ArrowUpRight size={14} className={`transition-transform ${open ? "rotate-90" : ""}`} />
            </button>
          )}
        </div>
      </div>
    </motion.article>
  );
}

function Section({ label, children, testId }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-8 py-6 md:py-8 border-t border-border" data-testid={testId}>
      <div className="md:col-span-3 font-mono text-[10px] uppercase tracking-[0.25em] text-accent">{label}</div>
      <div className="md:col-span-9 text-sm md:text-[15px] leading-relaxed text-foreground/85 space-y-3">{children}</div>
    </div>
  );
}

const Bullets = ({ items }) => (
  <ul className="space-y-2.5">
    {items.map((t, i) => (
      <li key={i} className="flex gap-3">
        <span className="mt-[9px] w-1.5 h-1.5 shrink-0 bg-accent" />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

const Numbered = ({ items }) => (
  <ol className="space-y-3">
    {items.map((t, i) => (
      <li key={i} className="flex gap-4">
        <span className="font-mono text-[11px] text-accent tabular-nums pt-[3px] w-6 shrink-0">{String(i + 1).padStart(2, "0")}</span>
        <span>{t}</span>
      </li>
    ))}
  </ol>
);

function CaseStudy({ p, onClose, onZoom }) {
  const ref = useRef(null);
  useEffect(() => {
    const t = setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    return () => clearTimeout(t);
  }, [p.code]);

  return (
    <motion.div
      ref={ref}
      id="case-study-panel"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      className="mt-8 md:mt-10 border border-accent/60 bg-surface p-6 md:p-10 scroll-mt-28"
      data-testid={`case-study-${p.code}`}
    >
      <div className="flex items-start justify-between gap-6">
        <div className="min-w-0">
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
            <span className="text-accent">{p.code}</span> · Case study
          </div>
          <h3 className="mt-3 font-heading text-2xl md:text-4xl font-black tracking-tight leading-[1.02] uppercase">{p.title}</h3>
          <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{p.scope}</div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close case study" data-testid="case-study-close" className="shrink-0 w-10 h-10 grid place-items-center border border-border hover:border-accent hover:text-accent transition-colors">
          <X size={16} />
        </button>
      </div>

      <div className="mt-8">
        <Section label="Context" testId="cs-context">{p.context.map((t, i) => <p key={i}>{t}</p>)}</Section>
        <Section label="Constraints" testId="cs-constraints"><Bullets items={p.constraints} /></Section>
        <Section label="Architecture" testId="cs-architecture">
          <p>{p.architecture}</p>
          <ArchitectureImage diagram={p.diagram} onZoom={onZoom} />
        </Section>
        <Section label="How it works" testId="cs-how"><Numbered items={p.howItWorks} /></Section>
        <Section label="Security controls" testId="cs-security"><Bullets items={p.securityControls} /></Section>
        <Section label="Results" testId="cs-results"><Bullets items={p.results} /></Section>
        <Section label="What I'd do differently" testId="cs-differently"><p>{p.differently}</p></Section>
        <Section label="Stack" testId="cs-stack"><p className="font-mono text-xs md:text-[13px] leading-relaxed text-foreground/75">{p.stack}</p></Section>
      </div>
    </motion.div>
  );
}

export default function Projects() {
  const [openCode, setOpenCode] = useState(null);
  const [zoom, setZoom] = useState(null);
  const active = CASE_STUDIES.find((p) => p.code === openCode);
  const featured = CASE_STUDIES.filter((p) => p.featured);
  const standard = CASE_STUDIES.filter((p) => !p.featured);

  return (
    <section id="projects" data-testid="projects-section" className="relative border-t border-border py-24 md:py-40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <ChapterHeader n="05" kicker="Selected work" title={<>Signal<br /><span className="text-accent">over noise</span>.</>}>
          <div className="space-y-12 md:space-y-16">
            <div>
              <div className="mb-6 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Featured case studies · 03</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="featured-grid">
                {featured.map((p, i) => (
                  <Card key={p.code} p={p} index={i} open={openCode === p.code} onToggle={setOpenCode} />
                ))}
              </div>
              <AnimatePresence mode="wait">
                {active && <CaseStudy key={active.code} p={active} onClose={() => setOpenCode(null)} onZoom={setZoom} />}
              </AnimatePresence>
            </div>
            <div>
              <div className="mb-6 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">More work · 05</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="standard-grid">
                {standard.map((p, i) => (
                  <Card key={p.code} p={p} index={i} />
                ))}
              </div>
            </div>
          </div>
        </ChapterHeader>
      </div>
      <Lightbox open={!!zoom} image={zoom?.image} title={zoom?.title} onClose={() => setZoom(null)} />
    </section>
  );
}
